import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  Snackbar,
  Stack,
  Switch,
  Tooltip,
  Typography,
  useTokens,
} from '@barracuda-internal/bds-core';

/**
 * PromptLoggingCard — lets support users (OPS/ADMIN) toggle prompt logging
 * for a specific account.
 *
 * Props:
 *   accountId      — account ID (used for wiring/display only in controlled mode)
 *   initialEnabled — initial toggle state
 *   role           — caller's AAD role: 'ADMIN' | 'OPS' | 'SUPPORT'
 *   onToggle       — called with (boolean) after the user confirms a change
 *   isLoading      — external loading flag (e.g. during a POST)
 */
const PromptLoggingCard = ({
  accountId,
  apiKeyName,
  initialEnabled = false,
  role = 'SUPPORT',
  onToggle,
  isLoading = false,
}) => {
  const tokens = useTokens();
  const [enabled, setEnabled] = useState(initialEnabled);

  // Sync local state when the parent's `initialEnabled` changes (e.g. after
  // the saga refreshes account_detail and the store's api_keys entry updates).
  useEffect(() => {
    setEnabled(initialEnabled);
  }, [initialEnabled]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, type: 'neutralSuccess', message: '' });

  const readOnly = role === 'SUPPORT';

  const handleChange = (e) => {
    const next = e.target.checked;
    if (next) {
      // Toggling ON requires confirmation
      setConfirmOpen(true);
    } else {
      // Toggling OFF is immediate — no compliance risk
      setEnabled(false);
      onToggle?.(false);
    }
  };

  const handleConfirm = () => {
    setConfirmOpen(false);
    setEnabled(true);
    onToggle?.(true);
  };

  const handleCancel = () => {
    setConfirmOpen(false);
    // Switch stays unchecked — no state change
  };

  const handleSnackbarClose = (_, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar((s) => ({ ...s, open: false }));
  };

  const switchEl = (
    <Switch
      checked={enabled}
      onChange={handleChange}
      disabled={readOnly || isLoading}
      inputProps={{ 'aria-label': 'Enable prompt logging' }}
    />
  );

  return (
    <>
      <Card bdsType="staticSubtle" bdsBoxShadow="default" bdsPadding="spacious">
        <Stack sx={{ gap: tokens.aliasesSpaceComponentDefault }}>
          <Typography variant="h4">
            Prompt logging{apiKeyName ? ` — ${apiKeyName}` : ''}
          </Typography>
          <Typography variant="captionSubtle" sx={{ maxWidth: 500 }}>
            When enabled, extracted prompts are persisted in scan events for this account.
            This is a compliance-sensitive setting. Only enable for accounts that have
            explicitly opted in.
          </Typography>

          <Stack
            direction="row"
            sx={{ gap: tokens.aliasesSpaceComponentDefault, alignItems: 'center' }}
          >
            {readOnly ? (
              <Tooltip title="Requires OPS or ADMIN role to change.">
                <span>{switchEl}</span>
              </Tooltip>
            ) : (
              switchEl
            )}
            <Typography variant="body1">{enabled ? 'Enabled' : 'Disabled'}</Typography>
          </Stack>

          {readOnly && (
            <Typography variant="captionSubtle">
              Requires OPS or ADMIN role to change.
            </Typography>
          )}
        </Stack>
      </Card>

      {/* Confirmation dialog — only shown when toggling ON */}
      <Dialog open={confirmOpen} onClose={handleCancel} bdsDialogWidth="small">
        <DialogTitle
          dialogTitle="Enable prompt logging?"
          showCloseIcon
          onClose={handleCancel}
        />
        <DialogContent
          content="Prompts for this account will be persisted in scan events until manually disabled. Confirm you have opt-in evidence from the tenant."
        />
        <DialogActions>
          <Button bdsType="interactiveNeutral" onClick={handleCancel}>
            Cancel
          </Button>
          <Button bdsType="interactiveEmphasis" onClick={handleConfirm}>
            Enable logging
          </Button>
        </DialogActions>
      </Dialog>

      {/* Feedback snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          bdsType={snackbar.type}
          bdsBoxShadowType="floating"
          onClose={handleSnackbarClose}
          sx={{ padding: tokens.aliasesSpaceContainerSpacious }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default PromptLoggingCard;
